// src/components/leitner/ResponseTypeInputs.jsx
// Response type selector and input components for different answer formats

import React, { useState } from 'react';
import {
  ListChecks, Type, AlignLeft, Link2, ArrowUpDown, Calculator, X,
  Plus, Trash2, ChevronUp, ChevronDown, GripVertical, CheckCircle, Brain
} from 'lucide-react';
import InlineMathField from '../math/InlineMathField';
import { MathRenderer } from '../math/MathRenderer';
import { SteppedCalculationCreator } from './SteppedCalculationInput';

import {
  RESPONSE_TYPES,
  RESPONSE_TYPE_CONFIG,
  ResponseTypeSelector
} from './ResponseBase';

export { RESPONSE_TYPES, RESPONSE_TYPE_CONFIG, ResponseTypeSelector };

const ResponseReasoningInput = ({
  reasoning = '',
  setReasoning,
  compact = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const isVisible = expanded || !!reasoning?.trim();

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        title={isVisible ? 'Hide Reasoning' : 'Add Reasoning'}
        className={`text-[11px] px-2 py-1 rounded border transition-colors inline-flex items-center gap-1 ${isVisible
          ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
          : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'
          }`}
      >
        {isVisible ? 'Hide Reasoning' : <Brain size={14} />}
      </button>

      {isVisible && (
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-400">Reasoning (optional)</label>
          <InlineMathField
            value={reasoning}
            onChange={setReasoning}
            rows={compact ? 2 : 3}
            placeholder="Explain the reasoning (Ctrl+M for math)"
            className="text-xs"
          />
          {reasoning?.trim() && (
            <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2">
              <div className="text-[10px] text-slate-500 mb-1">Preview</div>
              <div className="prose prose-xs prose-invert max-w-none text-slate-200">
                <MathRenderer content={reasoning} enableMarkdown={true} enableMath={true} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== MULTISELECT OPTIONS INPUT ==============
export const MultiselectInput = ({
  options,
  setOptions,
  correctIds,
  setCorrectIds,
  compact = false,
  availableQuestions = [],
  onQuickCreate,
  renderBranchCreator
}) => {
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [expandedReasoning, setExpandedReasoning] = useState(() => {
    const initial = {};
    options.forEach((option) => {
      initial[option.id] = !!option.reasoning?.trim();
    });
    return initial;
  });
  const [isCreatingBranch, setIsCreatingBranch] = useState(false);
  const [quickCreateText, setQuickCreateText] = useState('');
  const [quickCreateCorrect, setQuickCreateCorrect] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddOption = () => {
    if (options.length >= 8) return;
    setOptions([...options, { id: `opt_${Date.now()}`, text: '', reasoning: '', branchQuestionIds: [] }]);
  };

  const handleRemoveOption = (id) => {
    if (options.length <= 2) return;
    setOptions(options.filter(opt => opt.id !== id));
    setCorrectIds(correctIds.filter(cid => cid !== id));
  };

  const handleOptionTextChange = (id, text) => {
    setOptions(options.map(opt => opt.id === id ? { ...opt, text } : opt));
  };

  const handleOptionReasoningChange = (id, reasoning) => {
    setOptions(options.map(opt => opt.id === id ? { ...opt, reasoning } : opt));
  };

  const handleCorrectToggle = (id) => {
    setCorrectIds(
      correctIds.includes(id)
        ? correctIds.filter(cid => cid !== id)
        : [...correctIds, id]
    );
  };

  const toggleBranch = (optionId, questionId) => {
    setOptions(options.map(opt => {
      if (opt.id !== optionId) return opt;
      const branchIds = opt.branchQuestionIds || [];
      return {
        ...opt,
        branchQuestionIds: branchIds.includes(questionId)
          ? branchIds.filter(id => id !== questionId)
          : [...branchIds, questionId]
      };
    }));
  };

  const moveOption = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === options.length - 1)) return;
    const newOptions = [...options];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newOptions[index], newOptions[swapIndex]] = [newOptions[swapIndex], newOptions[index]];
    setOptions(newOptions);
  };

  const toggleReasoningInput = (optionId) => {
    setExpandedReasoning((prev) => ({
      ...prev,
      [optionId]: !prev[optionId]
    }));
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-slate-300">Answer Options</label>
      {options.map((option, index) => (
        <div key={option.id} className="space-y-2">
          <div className={`flex items-center gap-2 ${compact ? 'mb-1' : ''}`}>
            <div className="flex flex-col">
              <button type="button" onClick={() => moveOption(index, 'up')} disabled={index === 0}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronUp size={14} />
              </button>
              <button type="button" onClick={() => moveOption(index, 'down')} disabled={index === options.length - 1}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronDown size={14} />
              </button>
            </div>
            <div className="flex-grow">
              <InlineMathField
                value={option.text}
                onChange={(text) => handleOptionTextChange(option.id, text)}
                rows={1}
                placeholder={`Option ${index + 1} (Ctrl+M for math)`}
              />
            </div>

            <button
              type="button"
              onClick={() => setExpandedBranch(expandedBranch === option.id ? null : option.id)}
              className={`p-2 rounded-lg border transition-all flex items-center gap-1 ${(option.branchQuestionIds || []).length > 0
                ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'
                }`}
              title="Add Branch Questions"
            >
              <ArrowUpDown size={14} className="rotate-90" />
              <span className="text-[10px] uppercase font-bold">{(option.branchQuestionIds || []).length || ''}</span>
            </button>

            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={correctIds.includes(option.id)}
                onChange={() => handleCorrectToggle(option.id)}
                className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-green-500"
              />
              <span className="text-xs text-slate-400">✓</span>
            </label>
            <button
              type="button"
              onClick={() => toggleReasoningInput(option.id)}
              title={(expandedReasoning[option.id] || option.reasoning?.trim()) ? 'Hide Reasoning' : 'Add Reasoning'}
              className={`text-[11px] px-2 py-1 rounded border transition-colors ${(expandedReasoning[option.id] || option.reasoning?.trim())
                ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'
                }`}
            >
              {(expandedReasoning[option.id] || option.reasoning?.trim()) ? 'Hide Reasoning' : <Brain size={14} />}
            </button>
            {options.length > 2 && (
              <button type="button" onClick={() => handleRemoveOption(option.id)} className="p-1 text-red-400 hover:text-red-300">
                <Trash2 size={16} />
              </button>
            )}
          </div>

          {(expandedReasoning[option.id] || option.reasoning?.trim()) && (
            <div className={`ml-8 ${compact ? 'mt-1' : 'mt-2'} space-y-1`}>
              <label className="block text-xs font-medium text-slate-400">Reasoning (optional)</label>
              <InlineMathField
                value={option.reasoning || ''}
                onChange={(text) => handleOptionReasoningChange(option.id, text)}
                rows={2}
                placeholder="Explain the reasoning (Ctrl+M for math)"
                className="text-xs"
              />
              {option.reasoning?.trim() && (
                <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2">
                  <div className="text-[10px] text-slate-500 mb-1">Preview</div>
                  <div className="prose prose-xs prose-invert max-w-none text-slate-200">
                    <MathRenderer content={option.reasoning} enableMarkdown={true} enableMath={true} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Branch Question Selection */}
          {expandedBranch === option.id && (
            <div className="ml-8 p-3 bg-slate-900/50 border border-indigo-500/30 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-indigo-400 uppercase">Adaptive Branching</span>
                <span className="text-[10px] text-slate-500">{availableQuestions.length} Questions Available</span>
              </div>

              <div className="max-h-48 overflow-y-auto space-y-1 pr-2">
                {availableQuestions.length === 0 ? (
                  <p className="text-xs text-slate-500 italic text-center py-4">No other questions available to branch to.</p>
                ) : (
                  availableQuestions
                    .filter(q => q.id !== option.id) // Don't branch to self (basic check, though option.id isn't question.id)
                    .map(q => {
                      const isSelected = (option.branchQuestionIds || []).includes(q.id);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => toggleBranch(option.id, q.id)}
                          className={`w-full text-left p-2 rounded text-xs transition-all border ${isSelected
                            ? 'border-indigo-500 bg-indigo-500/10 text-white'
                            : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate flex-1">{q.text || q.stemText || 'Untitled Question'}</span>
                            {isSelected && <CheckCircle size={12} className="text-indigo-400" />}
                          </div>
                        </button>
                      );
                    })
                )}
              </div>
              <p className="text-[10px] text-slate-500 leading-tight">
                Learner will be directed to these questions sequentially if they select this option.
              </p>

              <div className="pt-2 border-t border-indigo-500/20">
                {!isCreatingBranch ? (
                  <button
                    type="button"
                    onClick={() => setIsCreatingBranch(true)}
                    className="w-full py-1.5 px-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold uppercase flex items-center justify-center gap-1 transition-colors"
                  >
                    <Plus size={12} />
                    Create New Branch Question
                  </button>
                ) : (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                    {renderBranchCreator ? (
                      renderBranchCreator(
                        async (questionData) => {
                          if (!onQuickCreate) return;
                          setIsSaving(true);
                          // Adapt the full question data for the existing onQuickCreate if it expects simpler args,
                          // or update onQuickCreate to handle full objects.
                          // For now, let's assume we want to call onQuickCreate with the full object.
                          const result = await onQuickCreate(questionData);
                          if (result) {
                            toggleBranch(option.id, result.id);
                            setIsCreatingBranch(false);
                          }
                          setIsSaving(false);
                        },
                        () => setIsCreatingBranch(false)
                      )
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-indigo-300 uppercase">Quick Create (Simple MCQ)</span>
                          <button
                            type="button"
                            onClick={() => setIsCreatingBranch(false)}
                            className="text-slate-500 hover:text-slate-300"
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <InlineMathField
                          value={quickCreateText}
                          onChange={setQuickCreateText}
                          placeholder="Question text..."
                          rows={1}
                          className="text-xs"
                        />
                        <InlineMathField
                          value={quickCreateCorrect}
                          onChange={setQuickCreateCorrect}
                          placeholder="Correct answer..."
                          rows={1}
                          className="text-xs"
                        />
                        <div className="flex gap-1">
                          <button
                            type="button"
                            disabled={isSaving || !quickCreateText.trim() || !quickCreateCorrect.trim()}
                            onClick={async () => {
                              if (!onQuickCreate) return;
                              setIsSaving(true);
                              const newOpt1 = { id: `opt_${Date.now()}_1`, text: quickCreateCorrect };
                              const newOpt2 = { id: `opt_${Date.now()}_2`, text: 'Sample Incorrect Answer' };
                              // Legacy onQuickCreate call
                              const newQuestion = await onQuickCreate(
                                quickCreateText,
                                [newOpt1, newOpt2],
                                [newOpt1.id]
                              );
                              if (newQuestion) {
                                toggleBranch(option.id, newQuestion.id);
                                setQuickCreateText('');
                                setQuickCreateCorrect('');
                                setIsCreatingBranch(false);
                              }
                              setIsSaving(false);
                            }}
                            className="flex-1 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded font-bold disabled:opacity-50"
                          >
                            {isSaving ? 'Saving...' : 'Create & Link'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
      {options.length < 8 && (
        <button type="button" onClick={handleAddOption} className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center">
          <Plus size={16} className="mr-1" /> Add Option
        </button>
      )}
    </div>
  );
};

// ============== SHORT ANSWER INPUT ==============
export const ShortAnswerInput = ({
  correctAnswer,
  setCorrectAnswer,
  acceptableAnswers = [],
  setAcceptableAnswers,
  caseSensitive = false,
  setCaseSensitive,
  reasoning = '',
  setReasoning,
  compact = false
}) => {
  const addAcceptable = () => setAcceptableAnswers([...acceptableAnswers, '']);
  const updateAcceptable = (index, value) => {
    const updated = [...acceptableAnswers];
    updated[index] = value;
    setAcceptableAnswers(updated);
  };
  const removeAcceptable = (index) => setAcceptableAnswers(acceptableAnswers.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">
          Correct Answer <span className="text-red-400">*</span>
        </label>
        <InlineMathField
          value={correctAnswer}
          onChange={setCorrectAnswer}
          rows={1}
          placeholder="Enter the correct answer (Ctrl+M for math)"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-slate-300">Alternative Answers</label>
          <button type="button" onClick={addAcceptable} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
            <Plus size={12} /> Add
          </button>
        </div>
        {acceptableAnswers.map((ans, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={ans}
              onChange={(e) => updateAcceptable(idx, e.target.value)}
              placeholder={`Alternative ${idx + 1}`}
              className="flex-1 px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm"
            />
            <button type="button" onClick={() => removeAcceptable(idx)} className="p-1 text-red-400 hover:text-red-300">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={caseSensitive}
          onChange={(e) => setCaseSensitive(e.target.checked)}
          className="w-4 h-4 rounded bg-slate-600 border-slate-500 text-indigo-500"
        />
        <span className="text-sm text-slate-300">Case sensitive</span>
      </label>

      <ResponseReasoningInput
        reasoning={reasoning}
        setReasoning={setReasoning}
        compact={compact}
      />
    </div>
  );
};

// ============== CALCULATION STEPS INPUT (Creator) ==============
export const CalculationStepsInput = ({
  steps = [],
  setSteps,
  finalAnswer = '',
  setFinalAnswer,
  reasoning = '',
  setReasoning,
  compact = false
}) => {
  const addStep = () => {
    if (steps.length >= 15) return;
    setSteps([...steps, { id: `step_${Date.now()}`, text: '', hint: '', reasoning: '' }]);
  };
  const [expandedStepReasoning, setExpandedStepReasoning] = useState(() => {
    const initial = {};
    steps.forEach((step) => {
      initial[step.id] = !!step.reasoning?.trim();
    });
    return initial;
  });

  const updateStep = (index, field, value) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], [field]: value };
    setSteps(updated);
  };

  const removeStep = (index) => {
    if (steps.length <= 1) return;
    setSteps(steps.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === steps.length - 1)) return;
    const newSteps = [...steps];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[swapIndex]] = [newSteps[swapIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const toggleStepReasoning = (stepId) => {
    setExpandedStepReasoning((prev) => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-300">Expected Steps (for AI grading reference)</label>
        <button
          type="button"
          onClick={addStep}
          disabled={steps.length >= 15}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 disabled:opacity-50"
        >
          <Plus size={12} /> Add Step
        </button>
      </div>

      <p className="text-xs text-slate-500">Define the expected working steps. AI will compare student steps against these.</p>

      <div className="space-y-2">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-start gap-2 p-2 bg-slate-700/50 rounded-lg border border-slate-600">
            <div className="flex flex-col gap-0.5 pt-2">
              <button type="button" onClick={() => moveStep(index, 'up')} disabled={index === 0}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronUp size={12} />
              </button>
              <button type="button" onClick={() => moveStep(index, 'down')} disabled={index === steps.length - 1}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronDown size={12} />
              </button>
            </div>

            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/30 border border-cyan-500/50 rounded-full text-xs text-cyan-300 font-medium mt-1">
              {index + 1}
            </span>

            <div className="flex-1 space-y-1">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <InlineMathField
                    value={step.text}
                    onChange={(text) => updateStep(index, 'text', text)}
                    rows={1}
                    placeholder={`Step ${index + 1} working... (Ctrl+M for math)`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => toggleStepReasoning(step.id)}
                  title={(expandedStepReasoning[step.id] || step.reasoning?.trim()) ? 'Hide Reasoning' : 'Add Reasoning'}
                  className={`text-[11px] px-2 py-1 rounded border transition-colors ${(expandedStepReasoning[step.id] || step.reasoning?.trim())
                    ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300'
                    : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500'
                    }`}
                >
                  {(expandedStepReasoning[step.id] || step.reasoning?.trim()) ? 'Hide Reasoning' : <Brain size={14} />}
                </button>
              </div>
              <input
                type="text"
                value={step.hint || ''}
                onChange={(e) => updateStep(index, 'hint', e.target.value)}
                placeholder="Optional hint for this step..."
                className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-slate-300 text-xs"
              />
              {(expandedStepReasoning[step.id] || step.reasoning?.trim()) && (
                <div className={`${compact ? 'mt-1' : 'mt-2'} space-y-1`}>
                  <label className="block text-xs font-medium text-slate-400">Reasoning (optional)</label>
                  <InlineMathField
                    value={step.reasoning || ''}
                    onChange={(text) => updateStep(index, 'reasoning', text)}
                    rows={2}
                    placeholder="Explain the reasoning (Ctrl+M for math)"
                    className="text-xs"
                  />
                  {step.reasoning?.trim() && (
                    <div className="rounded-md border border-slate-700 bg-slate-900/40 p-2">
                      <div className="text-[10px] text-slate-500 mb-1">Preview</div>
                      <div className="prose prose-xs prose-invert max-w-none text-slate-200">
                        <MathRenderer content={step.reasoning} enableMarkdown={true} enableMath={true} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {steps.length > 1 && (
              <button type="button" onClick={() => removeStep(index)} className="p-1 text-red-400 hover:text-red-300 mt-1">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Final Answer</label>
        <InlineMathField
          value={finalAnswer}
          onChange={setFinalAnswer}
          rows={1}
          placeholder="Expected final answer (Ctrl+M for math)"
        />
      </div>

      <ResponseReasoningInput
        reasoning={reasoning}
        setReasoning={setReasoning}
        compact={compact}
      />
    </div>
  );
};

// ============== PARAGRAPH INPUT ==============
export const ParagraphInput = ({
  rubric = '',
  setRubric,
  lineCount = 0,
  setLineCount,
  reasoning = '',
  setReasoning,
  compact = false
}) => {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Grading Rubric / Model Answer</label>
        <InlineMathField
          value={rubric}
          onChange={setRubric}
          placeholder="Describe what a good answer should include... (Ctrl+M for math)"
          rows={compact ? 2 : 3}
        />
        <p className="text-xs text-slate-500 mt-1">Shown to reviewers when grading</p>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">Answer Lines (0 = default)</label>
        <input
          type="number"
          value={lineCount}
          onChange={(e) => setLineCount(parseInt(e.target.value) || 0)}
          min="0"
          className="w-full px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Controls the response box height in previews and PDF exports.
        </p>
      </div>

      <ResponseReasoningInput
        reasoning={reasoning}
        setReasoning={setReasoning}
        compact={compact}
      />
    </div>
  );
};

// ============== MATCHING INPUT ==============
export const MatchingInput = ({
  pairs = [],
  setPairs,
  reasoning = '',
  setReasoning,
  compact = false
}) => {
  const addPair = () => {
    if (pairs.length >= 10) return;
    const id = Date.now();
    setPairs([...pairs, { id: `pair_${id}`, prompt: '', match: '' }]);
  };

  const updatePair = (index, field, value) => {
    const updated = [...pairs];
    updated[index] = { ...updated[index], [field]: value };
    setPairs(updated);
  };

  const removePair = (index) => {
    if (pairs.length <= 2) return;
    setPairs(pairs.filter((_, i) => i !== index));
  };

  const movePair = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === pairs.length - 1)) return;
    const newPairs = [...pairs];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newPairs[index], newPairs[swapIndex]] = [newPairs[swapIndex], newPairs[index]];
    setPairs(newPairs);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-300">Matching Pairs</label>
        <button type="button" onClick={addPair} disabled={pairs.length >= 10}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 disabled:opacity-50">
          <Plus size={12} /> Add Pair
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Items will be shuffled when displayed to students</p>

      <div className="space-y-2">
        {pairs.map((pair, index) => (
          <div key={pair.id} className="flex items-center gap-2 p-2 bg-slate-700/50 rounded-lg border border-slate-600">
            <div className="flex flex-col">
              <button type="button" onClick={() => movePair(index, 'up')} disabled={index === 0}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronUp size={12} />
              </button>
              <button type="button" onClick={() => movePair(index, 'down')} disabled={index === pairs.length - 1}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronDown size={12} />
              </button>
            </div>
            <span className="w-5 h-5 flex items-center justify-center bg-slate-600 rounded text-xs text-slate-300">{index + 1}</span>
            <input
              type="text"
              value={pair.prompt}
              onChange={(e) => updatePair(index, 'prompt', e.target.value)}
              placeholder="Left side..."
              className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm"
            />
            <Link2 size={14} className="text-slate-500" />
            <input
              type="text"
              value={pair.match}
              onChange={(e) => updatePair(index, 'match', e.target.value)}
              placeholder="Right side..."
              className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-white text-sm"
            />
            {pairs.length > 2 && (
              <button type="button" onClick={() => removePair(index)} className="p-1 text-red-400 hover:text-red-300">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {pairs.length < 2 && <p className="text-sm text-amber-400 mt-2">Add at least 2 pairs</p>}

      <ResponseReasoningInput
        reasoning={reasoning}
        setReasoning={setReasoning}
        compact={compact}
      />
    </div>
  );
};

// ============== ORDERING INPUT ==============
export const OrderingInput = ({
  items = [],
  setItems,
  reasoning = '',
  setReasoning,
  compact = false
}) => {
  const addItem = () => {
    if (items.length >= 10) return;
    setItems([...items, { id: `order_${Date.now()}`, text: '' }]);
  };

  const updateItem = (index, text) => {
    const updated = [...items];
    updated[index] = { ...updated[index], text };
    setItems(updated);
  };

  const removeItem = (index) => {
    if (items.length <= 2) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const moveItem = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === items.length - 1)) return;
    const newItems = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    setItems(newItems);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-300">Items (in correct order)</label>
        <button type="button" onClick={addItem} disabled={items.length >= 10}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 disabled:opacity-50">
          <Plus size={12} /> Add Item
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">Order shown here is the correct answer - items are shuffled for students</p>

      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={item.id} className="flex items-center gap-2">
            <div className="flex flex-col">
              <button type="button" onClick={() => moveItem(index, 'up')} disabled={index === 0}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronUp size={12} />
              </button>
              <button type="button" onClick={() => moveItem(index, 'down')} disabled={index === items.length - 1}
                className="p-0.5 text-slate-400 hover:text-slate-200 disabled:opacity-30">
                <ChevronDown size={12} />
              </button>
            </div>
            <span className="w-7 h-7 flex items-center justify-center bg-indigo-500/30 border border-indigo-500/50 rounded-full text-sm text-indigo-300 font-medium">
              {index + 1}
            </span>
            <input
              type="text"
              value={item.text}
              onChange={(e) => updateItem(index, e.target.value)}
              placeholder={`Step ${index + 1}...`}
              className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
            />
            {items.length > 2 && (
              <button type="button" onClick={() => removeItem(index)} className="p-1 text-red-400 hover:text-red-300">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {items.length < 2 && <p className="text-sm text-amber-400 mt-2">Add at least 2 items</p>}

      <ResponseReasoningInput
        reasoning={reasoning}
        setReasoning={setReasoning}
        compact={compact}
      />
    </div>
  );
};

// ============== UNIFIED RESPONSE INPUT RENDERER ==============
export const ResponseInput = ({
  responseType,
  responseData,
  setResponseData,
  availableQuestions = [],
  onQuickCreate,
  renderBranchCreator,
  compact = false
}) => {
  switch (responseType) {
    case RESPONSE_TYPES.MULTISELECT:
      return (
        <MultiselectInput
          options={responseData.options || []}
          setOptions={(options) => setResponseData({ ...responseData, options })}
          correctIds={responseData.correctOptionIds || []}
          setCorrectIds={(correctOptionIds) => setResponseData({ ...responseData, correctOptionIds })}
          compact={compact}
          availableQuestions={availableQuestions}
          onQuickCreate={onQuickCreate}
          renderBranchCreator={renderBranchCreator}
        />
      );

    case RESPONSE_TYPES.SHORT_ANSWER:
      return (
        <ShortAnswerInput
          correctAnswer={responseData.correctAnswer || ''}
          setCorrectAnswer={(correctAnswer) => setResponseData({ ...responseData, correctAnswer })}
          acceptableAnswers={responseData.acceptableAnswers || []}
          setAcceptableAnswers={(acceptableAnswers) => setResponseData({ ...responseData, acceptableAnswers })}
          caseSensitive={responseData.caseSensitive || false}
          setCaseSensitive={(caseSensitive) => setResponseData({ ...responseData, caseSensitive })}
          reasoning={responseData.reasoning || ''}
          setReasoning={(reasoning) => setResponseData({ ...responseData, reasoning })}
          compact={compact}
        />
      );

    case RESPONSE_TYPES.CALCULATION:
      return (
        <div className="space-y-3">
          <SteppedCalculationCreator
            steps={responseData.solutionSteps || [{ id: 'step_1', text: '', hint: '', reasoning: '' }]}
            setSteps={(solutionSteps) => setResponseData({ ...responseData, solutionSteps })}
            finalAnswer={responseData.finalAnswer || ''}
            setFinalAnswer={(finalAnswer) => setResponseData({ ...responseData, finalAnswer })}
            compact={compact}
          />
          <ResponseReasoningInput
            reasoning={responseData.reasoning || ''}
            setReasoning={(reasoning) => setResponseData({ ...responseData, reasoning })}
            compact={compact}
          />
        </div>
      );

    case RESPONSE_TYPES.PARAGRAPH:
      return (
        <ParagraphInput
          rubric={responseData.rubric || ''}
          setRubric={(rubric) => setResponseData({ ...responseData, rubric })}
          lineCount={responseData.lineCount || 0}
          setLineCount={(lineCount) => setResponseData({ ...responseData, lineCount })}
          reasoning={responseData.reasoning || ''}
          setReasoning={(reasoning) => setResponseData({ ...responseData, reasoning })}
          compact={compact}
        />
      );

    case RESPONSE_TYPES.MATCHING:
      return (
        <MatchingInput
          pairs={responseData.matchingPairs || [
            { id: 'pair_1', prompt: '', match: '' },
            { id: 'pair_2', prompt: '', match: '' }
          ]}
          setPairs={(matchingPairs) => setResponseData({ ...responseData, matchingPairs })}
          reasoning={responseData.reasoning || ''}
          setReasoning={(reasoning) => setResponseData({ ...responseData, reasoning })}
          compact={compact}
        />
      );

    case RESPONSE_TYPES.ORDERING:
      return (
        <OrderingInput
          items={responseData.orderingItems || [
            { id: 'order_1', text: '' },
            { id: 'order_2', text: '' },
            { id: 'order_3', text: '' }
          ]}
          setItems={(orderingItems) => setResponseData({ ...responseData, orderingItems })}
          reasoning={responseData.reasoning || ''}
          setReasoning={(reasoning) => setResponseData({ ...responseData, reasoning })}
          compact={compact}
        />
      );

    default:
      return null;
  }
};

export default {
  RESPONSE_TYPES,
  RESPONSE_TYPE_CONFIG,
  ResponseTypeSelector,
  ResponseInput,
  MultiselectInput,
  ShortAnswerInput,
  ParagraphInput,
  MatchingInput,
  OrderingInput
};
